import React, { createContext, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { FaCaretRight } from 'react-icons/fa';

/**
 * Controlled multi-open Accordion (react-bootstrap `Accordion alwaysOpen` equivalent).
 *
 * - `activeKeys` (controlled) / `defaultActiveKeys` (uncontrolled) hold the OPEN
 *   item keys. Keys are normalized with String() so numeric and string keys
 *   interoperate (e.g. Redux defaults use numbers, expand-all uses strings).
 * - `onChange` fires with the FULL next keys array (string[]), matching the
 *   react-bootstrap `onSelect` array form used across the app.
 * - Item bodies stay MOUNTED when closed (hidden with tw-hidden) so form inputs,
 *   editors, and registered fields inside are never unmounted.
 */
const AccordionContext = createContext(null);

const Accordion = ({
  activeKeys,
  defaultActiveKeys = [],
  onChange,
  className = '',
  children,
}) => {
  const [internalKeys, setInternalKeys] = useState(defaultActiveKeys);

  const currentKeys = activeKeys !== undefined ? activeKeys : internalKeys;
  const openSet = new Set((currentKeys || []).map(String));

  const toggle = (eventKey) => {
    const key = String(eventKey);
    const nextKeys = openSet.has(key)
      ? (currentKeys || []).filter((k) => String(k) !== key)
      : [...(currentKeys || []), key];

    if (activeKeys === undefined) {
      setInternalKeys(nextKeys);
    }
    onChange?.(nextKeys);
  };

  return (
    <AccordionContext.Provider value={{ openSet, toggle }}>
      <div className={`tw-border tw-border-slate-200 tw-rounded-lg tw-overflow-hidden tw-bg-white ${className}`}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
};

const AccordionItem = ({
  eventKey,
  header,
  headerClassName = '',
  bodyClassName = '',
  className = '',
  children,
}) => {
  const ctx = useContext(AccordionContext);
  const isOpen = ctx?.openSet.has(String(eventKey)) ?? false;

  return (
    <div className={className}>
      {/* type="button" is mandatory — accordions can live inside large forms */}
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => ctx?.toggle(eventKey)}
        className={`tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-slate-50 hover:tw-bg-slate-100 tw-border-0 tw-border-b tw-border-solid tw-border-slate-200 tw-cursor-pointer tw-text-left tw-text-sm tw-font-semibold tw-text-primary-600 tw-transition-colors ${headerClassName}`}
      >
        <FaCaretRight
          size={12}
          className={`tw-flex-shrink-0 tw-transition-transform tw-duration-150 ${isOpen ? 'tw-rotate-90' : ''}`}
        />
        <span className="tw-flex-1">{header}</span>
      </button>
      {/* Body stays mounted when closed — do not change to conditional render */}
      <div className={`${isOpen ? '' : 'tw-hidden'} ${bodyClassName}`}>
        {children}
      </div>
    </div>
  );
};

Accordion.Item = AccordionItem;

Accordion.propTypes = {
  activeKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  defaultActiveKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

AccordionItem.propTypes = {
  eventKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  header: PropTypes.node,
  headerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Accordion;
